import React, { ReactElement } from 'react';
import { createUseStyles } from 'react-jss';
import Heading from '@src/components/SectionHeading';

const TOS_HISTORY = [
  {
    label: 'September 1st, 2020',
    url: 'https://cdn.a.team/contracts/standard/terms-of-services/ateam-terms-of-services-2020-09-01.pdf',
  },
];

const useStyles = createUseStyles({
  root: {
    lineHeight: '24px',
    '& div': {
      marginBottom: '1em',
    },
  },
});

const SectionHeading = ({
  children,
  isFirst,
}: {
  children: React.ReactNode;
  isFirst?: boolean;
}) => {
  return (
    <Heading
      isFirst={isFirst}
      style={{
        textAlign: 'left',
      }}
    >
      {children}
    </Heading>
  );
};

const SectionSubHeading = ({ children }: { children: React.ReactNode }) => {
  return (
    <h5 style={{ fontSize: '16px', fontWeight: 'bold', display: 'inline' }}>
      {children}
    </h5>
  );
};

export default function TermsOfServiceV2(): ReactElement {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <SectionHeading isFirst>
        <div>ATeams</div>
        <div>Platform Terms of Service Agreement</div>
      </SectionHeading>
      <div>
        This Terms of Service Agreement (“<strong>Agreement</strong>”) is an
        agreement between you and 2022 Services LLC. and governs your use of
        ATeams' proprietary Platform Services that match Builders with Clients
        (each as defined below). This Agreement governs only the use of the
        Platform Services and expressly does not govern the use of the
        Development Services (as defined below). The Development Services are
        governed by the agreements contemplated in Section 6 of this Agreement.
        ATeams reserves the right to modify this Agreement at our discretion and
        will notify you of any such changes on the website or via the email
        address in your Registration Data.
      </div>
      <div>
        The term "you" means the user of the Platform Services whether you are a
        Builder or a Client. To use the Platform, you must agree to this
        Agreement, which can be found{' '}
        <a href="https://platform.a.team/terms-of-service">here</a>. TO ACCESS
        AND USE PLATFORM SERVICES, YOU MUST REVIEW AND ACCEPT THE TERMS OF THIS
        AGREEMENT BY CLICKING ON THE "I ACCEPT" BUTTON. ONCE ACCEPTED, THIS
        AGREEMENT BECOMES A BINDING LEGAL COMMITMENT BETWEEN YOU AND ATEAMS,
        INC. ("ATEAMS").
      </div>
      <div>
        YOU REPRESENT THAT: (1) YOU HAVE READ, UNDERSTAND, AND AGREE TO BE BOUND
        BY THE TERMS OF THIS AGREEMENT AND (2) YOU HAVE THE AUTHORITY TO ENTER
        INTO THE AGREEMENT ON BEHALF OF BUILDER OR CLIENT, AS APPLICABLE. IF YOU
        DO NOT AGREE TO BE BOUND BY THESE TERMS, YOU SHOULD NOT CLICK THE "I
        ACCEPT" BUTTON.
      </div>
      <SectionHeading>1. Platform Services</SectionHeading>
      <div>
        The Platform Services reside on a platform whereby developers, project
        managers, product managers and other technology specialists ("Builders")
        are matched with companies that are building certain technology
        ("Clients") and are seeking to engage technology specialists on a
        project or staff augmentation basis (collectively, a "Project"). If you
        are a Builder, you will provide required information about you,
        including your experience, skillsets and payment requirements that
        ATeams may use to promote your services to Clients. If you are a Client,
        you will provide required information about you and the applicable
        project(s) for which you are seeking a match with Builders including,
        your products and services, the skillsets and experience required of
        Builders, the number of Builders you need and the applicable budget. The
        information that you provide to us in connection with your use of the
        Platform Services is called "your Content". As further described in this
        Agreement and in the Platform Services themselves, ATeams may promote
        through the Platform Services your Content for the purpose of
        establishing a match between Client requirements and Builder skillsets.
        ATeams does not guarantee that any matches will be made through the
        Platform Services. If you are Builder, in addition to being called
        "you", you may also in this Agreement be called "Builder". If you are
        Client, in addition to being called "you", you may also in this
        Agreement be called "Client".
      </div>
      <SectionHeading>2. Accounts</SectionHeading>
      <div>
        <div>
          <SectionSubHeading>2.1. Registration.</SectionSubHeading>
          Use of and access to the Platform Services may require registration of
          an account for the Services ("Account"). In registering an Account,
          you agree to (a) provide true, accurate, current and complete
          information ("Registration Data") and (b) maintain and promptly update
          the Registration Data to keep it true, accurate, current and complete.
          The Registration Data is a subset of your Content. You may not have
          more than one Account at any given time. You may not create an Account
          or use the Platform Service if you have been previously removed by
          ATeams or previously banned from the Platform Services.
        </div>
        <div>
          <SectionSubHeading>2.2. Access Credentials.</SectionSubHeading>
          When you register for an Account, users acting on your behalf (your
          "Permitted Users") will create or receive login information,
          passwords, security protocols, and policies through which Permitted
          Users access the Platform Services ("Access Credentials"). You will
          safeguard, and ensure that all Permitted Users safeguard, the Access
          Credentials. You will be responsible for all acts and omissions of
          your Permitted Users. You will notify ATeams immediately if you or any
          of your Permitted Users learn of any unauthorized use of any Access
          Credentials or any other known or suspected breach of security of the
          Platform Services. As between you and ATeams: (a) ATeams owns any
          Access Credentials in connection with the Platform Services, and may
          revoke use of, or change, a Permitted User's Access Credentials and
          (b) may reject, revoke or delete any Account, in each case at any
          time, without notice, and at its discretion.
        </div>
        <div>
          <SectionSubHeading>2.3 Account Verification.</SectionSubHeading>
          When you register for an Account or at any other time, your Account
          will be subject to verification, including validation against
          third-party databases or verification of one or more official
          government or legal documents that confirm your identity, location,
          and ability to act on behalf of your business. You authorize ATeams,
          directly or through third parties, to make any inquiries necessary to
          validate your identity, location, and ownership of your email address
          or financial accounts, subject to applicable law. When requested, you
          must timely provide ATeams with complete information about you and
          your business, which includes, but is not limited to, providing
          official government or legal documents.
        </div>
      </div>
      <SectionHeading>3. Your Responsibilities</SectionHeading>
      <div>
        <div>
          <SectionSubHeading>
            3.1. Your Content Must be True and Accurate.
          </SectionSubHeading>
          When using the Platform Services, you represent and warrant that your
          Content is true and accurate, is not false or misleading and that you
          will ensure that it does not become inaccurate, false or misleading.
          If you provide any information that is untrue, misleading, not current
          or incomplete, or ATeams has reasonable grounds to suspect that any
          information you provided is untrue, misleading, not current or
          incomplete, ATeams has the right to suspend or terminate your Account
          and use of the Platform Services.
        </div>
        <div>
          <SectionSubHeading>3.2. Responsibility for Match.</SectionSubHeading>
          If you are a Client, you are solely responsible to determine if a
          particular Builder is an appropriate resource for any of your
          Projects. If you are a Builder, you are solely responsible to
          determine if a particular Project and the applicable Client is a good
          fit. If you determine that Client, Project and Builder are a good fit,
          you as Builder or Client, as applicable, may seek to enter into
          agreements with A-Teams, as outlined in Section 6 below, pursuant to
          which Builder performs specified development services for the benefit
          of Client ("Development Services").
        </div>
        <div>
          <SectionSubHeading>3.3. Restrictions.</SectionSubHeading>
          You will not, and will not permit any Permitted User or third party
          to: (a) provide access to the Platform Services to third parties or
          use the Platform Services for the benefit of third parties, including,
          for the purpose of providing information about Builders, Clients or
          Projects to third parties; (b) modify the Platform Services or any
          documentation, or create any derivative product thereof; (c) assign,
          sublicense, sell, resell, lease, rent, or otherwise transfer or
          convey, or pledge as security or otherwise encumber ATeams' rights in
          the Platform Services; (d) harvest, collect, gather or assemble
          information or data regarding other users; (e) upload any of your
          Content or otherwise use the Platform Services or any information or
          data received through or in connection with the Platform Services in a
          manner that (i) may infringe or violate the intellectual property or
          other rights of any individual or entity, including without limitation
          the rights of publicity or privacy; (ii) may violate applicable laws
          or governmental regulations; (iii) is unlawful, threatening, abusive,
          harassing, misleading, false, defamatory, libelous, deceptive,
          fraudulent, invasive of another's privacy, tortious, obscene,
          offensive, profane or racially, ethnically, or otherwise
          discriminatory; (iv) constitutes unauthorized or unsolicited
          advertising, junk or bulk e-mail; (v) impersonates any person or
          entity, including any employee or representative of ATeams; (vi)
          interferes with or attempts to interfere with the proper functioning
          of the Platform Services or uses the Platform Services in any way not
          expressly permitted by this Agreement or (vii) attempts to engage in
          or engages in, any potentially harmful acts that are directed against
          the Platform Services; (f) modify, adapt, alter, translate, or create
          derivative works from the Platform Services; (g) reverse engineer,
          decompile, disassemble, or otherwise attempt to derive the source code
          for the Platform Services, except and only to the extent that such
          activity is expressly permitted by applicable law notwithstanding this
          limitation; (h) remove, alter, cover or obfuscate any copyright
          notices or other proprietary rights notices included in the Platform
          Services.
        </div>
      </div>
      <SectionHeading>4. Intellectual Property</SectionHeading>
      <div>
        <div>
          <SectionSubHeading>4.1. License in Your Content.</SectionSubHeading>
          As between you and ATeams, you own all right, title and interest in
          your Content. You hereby grant to ATeams a fully paid, royalty-free,
          worldwide, non-exclusive right and license to use, distribute,
          reproduce, modify, adapt, publicly perform, and publicly display any
          of your Content for the purposes of operating and providing the
          Platform Services – and to allow third parties acting on ATeams'
          behalf to do the same. Without limiting the preceding, such third
          parties may include artificial intelligence services, provided that
          any such AI service is required to use your Content in accordance with
          their most protective privacy settings (e.g., it won't use your
          Content to train models and the data is retained for the shortest
          period required by the third party provider). You also agree that
          ATeams may process your Content with the content of others to create
          and compile aggregated data and/or statistics, and to use the
          aggregated data and statistics in connection with its business
          (including distributions to third parties) provided that such
          aggregated data and statistics will not enable you to be identified.
          Except for the limited licenses in your Content granted to ATeams
          herein, you retain all right, title and interest in your Content.
        </div>
        <div>
          <SectionSubHeading>4.2. Grant of Access.</SectionSubHeading>
          Subject to the terms and conditions of these Terms, ATeams grants you
          a non-exclusive, non-transferable, right to permit your Permitted
          Users to access and use the Platform Services solely for your internal
          use and not for the benefit of any third party. Except for the limited
          access rights in the Platform Services granted to you herein, ATeams
          retains all right, title and interest in the Platform and the
          Services.
        </div>
        <div>
          <SectionSubHeading>4.3. Feedback.</SectionSubHeading>
          You hereby grant to ATeams a fully paid, royalty-free, perpetual,
          irrevocable, worldwide, non-exclusive, and fully sublicensable right
          and license to use, reproduce, perform, display, distribute, adapt,
          modify, re-format, create derivative works of, and otherwise
          commercially or non-commercially exploit in any manner any and all
          feedback, suggestions, or ideas (collectively, "Feedback") related to
          the Services or ATeams' products, services, or business that you or
          your Permitted Users provide, in connection with the operation,
          maintenance, and improvement of the Services and/or ATeams' business.
        </div>
        <div>
          <SectionSubHeading>
            4.4. Intellectual Property Used and Developed in Connection with the
            Development Services.
          </SectionSubHeading>
          The rights owned and granted in the intellectual property and
          intellectual property rights used and developed in connection with the
          Development Services are governed by the Development Services
          Agreements contemplated in Section 6.
        </div>
      </div>
      <SectionHeading>5. Investigations</SectionHeading>
      <div>
        If ATeams becomes aware of any possible violations by you or your
        Permitted Users of this Agreement, ATeams reserves the right, but has no
        obligation, to investigate such violations. If, as a result of the
        investigation, ATeams believes that criminal activity has occurred,
        ATeams reserves the right to refer the matter to, and to cooperate with,
        any and all applicable legal authorities. ATeams is entitled, except to
        the extent prohibited by applicable law, to disclose any information or
        materials on or in connection with the Platform Services, including your
        Content to comply with applicable laws, legal process or governmental
        request; (b) respond to any claims that your Content and /or you and
        your Permitted Users' acts or omissions violate the rights of third
        parties or (c) protect the rights, property or personal safety of
        ATeams, its Users or the public, and all enforcement or other government
        officials.
      </div>
      <SectionHeading>6. Contracts for Development Services.</SectionHeading>
      <div>
        In the event that Client and Builder would like to move forward with a
        Project, each of Client and Builder should notify ATeams. If ATeams
        approves the Project:
        <ol>
          <li>
            Client and A-Teams will enter into a service order (the "Client
            Service Order") that specifies the Development Services to be
            performed, the fees to be paid by Client, the anticipated length of
            the Project and other material business and technical provisions.
            The applicable Client Service Order is governed by the ATeams –
            Client Development Services Terms and Conditions set forth at:
            [ATeams to insert URL] and
          </li>
          <li>
            Builder and A-Teams will enter into a service order (the "Builder
            Service Order") that specifies the Development Services to be
            performed, the fees to be paid to Builder, the anticipated length of
            the Project and other material business and technical provisions.
            The applicable Builder Service Order is governed by the
            ATeams-Builder Development Services Terms and Conditions set forth
            at: [ATeams to insert URL].
          </li>
          <li>
            The Builder Service Order, the ATeams – Builder Development Services
            Terms and Conditions, the Client Service Order and the ATeams –
            Client Development Services Terms and Conditions are collectively
            referred to as the "Development Services Agreements".
          </li>
          <li>
            In the event of any conflict between the provisions of this
            Agreement and the Development Service Agreements, the provision in
            the Development Services Agreement will prevail.
          </li>
        </ol>
      </div>
      <SectionHeading>7. Confidential Information.</SectionHeading>
      <div>
        <SectionSubHeading>7.1. "Confidential Information"</SectionSubHeading>
        means all written or oral information, disclosed by or on behalf of
        either you or ATeams to the other that has been identified as
        confidential or that by the nature of the information or circumstances
        surrounding disclosure would be reasonably understood to be confidential
        or proprietary. Your Content is your Confidential Information.
      </div>
      <div>
        <SectionSubHeading>7.2. Obligations of each Party.</SectionSubHeading>
        ATeams and you each agree: (a) to use Confidential Information disclosed
        by the other party only for the purposes of providing and using the
        Platform Services and as otherwise expressly provided for in this
        Agreement; (b) that such party will not otherwise reproduce or use
        Confidential Information disclosed by the other and will hold in
        confidence and protect such Confidential Information from dissemination
        to, and use by, any third party, with the understanding that ATeams
        will, if you are Builder, disclose your Confidential Information to
        users that are ATeam clients and if you are a Client, disclose your
        Confidential Information to users that are ATeam builders; (c) to
        restrict access to the Confidential Information disclosed by the other
        party to such of its personnel, agents, and/or consultants, who have a
        need to have access and who have been advised of, and have agreed in
        writing to treat such information in a manner consistent with the
        obligations of this Section. For the avoidance of doubt: (a) if you are
        a Builder, you agree to treat the Confidential Information of Clients in
        the same manner as you are required to treat ATeams' Confidential
        Information; (b) if are a Client, you agree to treat the Confidential
        Information of a Builder in the same manner as you are required treat
        ATeams' Confidential Information; (c) in no event is ATeams' liable for
        the acts and omission of either Builders or Clients in the context of
        this Section 7; and (d) each Buyer makes each Client and each Client
        makes each Buyer an intended third party beneficiary of its obligations
        pursuant to this Section 7.
      </div>
      <div>
        <SectionSubHeading>7.3. Confidentiality Exceptions.</SectionSubHeading>
        Notwithstanding the foregoing, the provisions of Section 7.1 will not
        apply to Confidential Information that (a) is publicly available or in
        the public domain at the time disclosed; (b) is or becomes publicly
        available or enters the public domain through no fault of the recipient;
        (c) is rightfully communicated to the recipient by persons not bound by
        confidentiality obligations with respect thereto; (d) is already
        rightfully in the recipient's possession free of any confidentiality
        obligations with respect thereto at the time of disclosure; (e) is
        independently developed by the recipient without use of or reference to
        the other party's Confidential Information; or (f) is approved for
        release or disclosure by the Disclosing party without restriction.
        Notwithstanding the foregoing, each party may disclose Confidential
        Information to the limited extent required (i) in order to comply with
        the order of a court or other governmental body, or as otherwise
        necessary to comply with applicable law, provided that the party making
        the disclosure pursuant to the order will first have given written
        notice to the other party (to the extent legally permitted) and made a
        reasonable effort to obtain a protective order; or (ii) to establish a
        party's rights under this Agreement, including to make such court
        filings as it may be required to do.
      </div>
      <SectionHeading>8. No Solicitation.</SectionHeading>
      <div>
        You may not use the Services to solicit for any other business, website
        or services. You may not solicit, advertise for, or contact Clients or
        Builders – or other ATeams clients or builders – for employment,
        contracting, or any other purpose not related to the Platform Services
        and the Development Services.
      </div>
      <SectionHeading>9. Non-Circumvention.</SectionHeading>
      <div>
        <SectionSubHeading>9.1. Non-Circumvention Period.</SectionSubHeading>
        The value of the Services rests in its thriving and symbiotic matching
        of Builders and Clients. You acknowledge and agree that a substantial
        portion of the compensation ATeams receives for providing the Platform
        Services is the payment of fees collected through Clients' purchase of
        Development Services as contemplated in Section 6. Therefore, for 24
        months from the date that you last upload your Content into the Platform
        Services (the "Non-Circumvention Period"), you agree to use the Platform
        Services as your exclusive method to request, make, and receive all
        payments for work directly or indirectly with a Builder or Client, as
        applicable. For the avoidance of doubt, if you did not identify, or were
        not identified by, another party through the Services, i.e., if a
        Builder and Client worked together before being introduced or matched
        through the Platform Services, then the Non-Circumvention Period does
        not apply with respect to you and such other party. If you use the
        Platform Services as an employee, agent, or representative of another
        business, then the Non-Circumvention Period applies to you and other
        employees, agents, or representatives of the business when acting in
        that capacity with respect to the other User. For the avoidance of
        doubt, if you enter into a Development Services arrangement, the
        non-Circumvention provisions specified in that Agreement will apply in
        addition to the provisions of this Section 9.
      </div>
      <div>
        <SectionSubHeading>9.2. Restrictions.</SectionSubHeading>
        You agree not to: (a) solicit proposals or submit proposals to parties
        identified through the Services to contract, hire, work with, or pay
        outside the Platform Services; (b) accept proposals or solicit parties
        identified through the Platform Services to contract, invoice, or
        receive payment outside the Services; (c) refer a User identified on the
        Services to a third-party who is not a user of the Services for any
        purpose, including making or receiving payments in a manner that ATeams
        does not receive the Service Fee. You will notify ATeams immediately
        upon becoming aware of a breach or potential breach of this
        non-circumvention provision.
      </div>
      <div>
        <SectionSubHeading>9.3. Connection Fee.</SectionSubHeading>
        The non-circumvention obligation in this Section 9 will not apply to you
        with respect to an ATeams Relationship only if the applicable Builder
        and Client collectively pay ATeams a fee for such relationship (the
        "Network Fee"), including if you want to commence or continue working
        with the same Client or Builder, as applicable, upon termination or
        expiration of the applicable Service Order. The Network Fee is computed
        as follows: (a) the greater of (i) twenty thousand dollars ($20,000) or
        (ii) the amount that is equal to 3 times (3x) the anticipated monthly
        compensation paid to the Builder, plus (c) ten percent (10%) of any
        one-time signing bonus or similar additional compensation.
      </div>
      <SectionHeading>10. Indemnification.</SectionHeading>
      <div>
        You agree to defend, indemnify and hold ATeams, its parents,
        subsidiaries, affiliates, officers, employees, agents, partners,
        suppliers, and licensors (each, an "ATeams Party" and collectively, the
        "ATeams Parties") harmless from any losses, costs, liabilities and
        expenses (including reasonable attorneys' fees) relating to or arising
        out of any and all of the following: (a) your Content; (b) your use of
        the Platform Services; (c) your violation of the Agreement or of any
        rights of another party, including any other users; or (d) your
        violation of any applicable laws, rules or regulations. ATeams reserves
        the right, at its own cost, to assume the exclusive defense and control
        of any matter otherwise subject to indemnification by you, in which
        event you will fully cooperate with ATeams in asserting any available
        defenses. For purposes of this Section 10, you includes any of your
        Permitted Users or agents.
      </div>
      <SectionHeading>11. Disclaimer of Warranties.</SectionHeading>
      <div>
        <SectionSubHeading>11.1. As Is.</SectionSubHeading>
        YOUR USE OF THE PLATFORM SERVICES ARE AT YOUR SOLE RISK. THE SERVICES
        ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITH ALL FAULTS.
        ATEAMS PARTIES EXPRESSLY DISCLAIM ALL WARRANTIES, REPRESENTATIONS, AND
        CONDITIONS OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT
        LIMITED TO, THE IMPLIED WARRANTIES OR CONDITIONS OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. THE ATEAMS
        PARTIES MAKE NO WARRANTY, REPRESENTATION OR CONDITION THAT THE SERVICES
        OR ANY SERVICES OBTAINED OR TRANSACTIONS MADE IN CONNECTION THEREWITH
        WILL MEET YOUR REQUIREMENTS.
      </div>
      <div>
        <SectionSubHeading>
          11.2. Third Parties or Other Users.
        </SectionSubHeading>
        YOU ACKNOWLEDGE AND AGREE THAT ATEAMS PARTIES ARE NOT LIABLE, AND YOU
        AGREE NOT TO SEEK TO HOLD ATEAMS PARTIES LIABLE, FOR THE CONDUCT OF
        THIRD PARTIES, AND THAT THE RISK OF INJURY FROM SUCH THIRD PARTIES RESTS
        ENTIRELY WITH YOU. YOU ARE SOLELY RESPONSIBLE FOR ALL OF YOUR
        COMMUNICATIONS AND INTERACTIONS WITH OTHER USERS OF THE SERVICES.
      </div>
      <SectionHeading>12. Limitation of Liability.</SectionHeading>
      <div>
        EXCEPT AS MAY BE PROVIDED BY APPLICABLE LAW, IN NO EVENT WILL ATEAMS
        PARTIES BE LIABLE FOR (a) ANY INDIRECT, INCIDENTAL, SPECIAL, OR
        CONSEQUENTIAL DAMAGES, OR DAMAGES OR COSTS DUE TO LOSS OF PRODUCTION OR
        USE, ANY BUSINESS INTERRUPTION, OR ANY PROCUREMENT OF SUBSTITUTE GOODS
        OR SERVICES, IN EACH CASE WHETHER OR NOT ATEAMS HAS BEEN ADVISED OF THE
        POSSIBILITY OF SUCH DAMAGES, ARISING OUT OF OR IN CONNECTION WITH THESE
        TERMS OF SERVICE OR ANY COMMUNICATIONS, INTERACTIONS OR MEETINGS WITH
        OTHER USERS, ON ANY THEORY OF LIABILITY OR (b) ANY OTHER AMOUNTS THAT
        ARE, IN THE AGGREGATE, GREATER THAN $50. THE LIMITATIONS OF DAMAGES SET
        FORTH ABOVE ARE FUNDAMENTAL ELEMENTS OF THE BASIS OF THE BARGAIN BETWEEN
        ATEAMS AND YOU.
      </div>
      <SectionHeading>13. Term and Termination</SectionHeading>
      <div>
        <div>
          <SectionSubHeading>13.1. Term.</SectionSubHeading>
          The Agreement commences on the Effective Date and remain in full force
          and effect until terminated by either party in accordance with this
          Agreement.
        </div>
        <div>
          <SectionSubHeading>13.2. Termination for Breach.</SectionSubHeading>
          Either party may terminate this Agreement upon written notice to the
          other party if the other party breaches this Agreement and does not
          cure such breach within fifteen (15) days of receiving notice thereof.
        </div>
        <div>
          <SectionSubHeading>
            13.3. Termination for Convenience.
          </SectionSubHeading>
          With or without notice, ATeams may terminate this Agreement at its
          convenience at any time for any reason or no reason.
        </div>
        <div>
          <SectionSubHeading>13.4. Effect of Termination.</SectionSubHeading>
          Termination of this Agreement does not terminate or otherwise impact
          any Development Services Agreement entered into between you and ATeams
          (see Section 6). Upon the termination or expiration of this Agreement
          for any reason, the terms and provisions forth in Sections 3.3, and 4,
          5, and 7 through 16 will survive.
        </div>
        <div>
          <SectionSubHeading>13.5. Notification.</SectionSubHeading>
          If ATeams decides to temporarily or permanently close your Account,
          ATeams has the right where allowed by law but not the obligation to:
          (a) notify other users of the Platform Services or the Development
          Services and (b) provide those users with a summary of the reasons for
          your account closure. you agree that ATeams will have no liability
          arising from or relating to any notice that it may provide to any User
          regarding closed account status or the reason(s) for the closure.
        </div>
      </div>
      <SectionHeading>14. Dispute Resolution</SectionHeading>
      <div>
        All claims and disputes arising out of or relating to the Agreement will
        be litigated exclusively in the state or federal courts located in New
        York, NY. This Agreement and any action related thereto will be governed
        and interpreted by and under the laws of the State of New York, New
        York, consistent with the Federal Arbitration Act, without giving effect
        to any principles that provide for the application of the law of another
        jurisdiction. The United Nations Convention on Contracts for the
        International Sale of Goods does not apply to the Agreement.
      </div>
      <SectionHeading>15. Construction</SectionHeading>
      <div>
        Section headings are included in this Agreement merely for convenience
        of reference; they are not to be considered part of this Agreement or
        used in the interpretation of this Agreement. When used in this
        Agreement, “including” means “including without limitation.” No rule of
        strict construction will be applied in the interpretation or
        construction of this Agreement.
      </div>
      <SectionHeading>16. General Provisions</SectionHeading>
      <div>
        The Agreement, and your rights and obligations hereunder, may not be
        assigned, subcontracted, delegated or otherwise transferred by you
        without ATeams’ prior written consent, and any attempted assignment,
        subcontract, delegation, or transfer in violation of the foregoing will
        be null and void. In the context of the foregoing sentence any change of
        control constitutes an assignment. Nothing in this Agreement is intended
        to or should be construed to create a partnership, joint venture,
        franchisor/franchisee or employer-employee relationship between ATeams
        and you. ATeams will not be liable for any delay or failure to perform
        resulting from causes outside its reasonable control, including, but not
        limited to, acts of God, war, natural disasters, disease, terrorism,
        riots, embargos, acts of civil or military authorities, fire, floods,
        accidents, strikes or shortages of transportation facilities, fuel,
        energy, labor or materials. you may give notice to ATeams at the
        following address: 88 University Place, 5th Floor, New York, NY 10003.
        ATeams may give notice to you at the latest address known to ATeams,
        including the applicable email address. You represent and warrant that
        you will provide written notice to ATeams of any change in physical or
        email address for the purpose of notice. Such notice will be deemed
        given when received by the receiving party by letter delivered by
        nationally recognized overnight delivery service or first class postage
        prepaid mail at the above address, or if ATeams notice is sent via
        email, at the time the email is sent by ATeams. Any waiver or failure to
        enforce any provision of the Agreement on one occasion will not be
        deemed a waiver of any other provision or of such provision on any other
        occasion. If any portion of this Agreement is held invalid or
        unenforceable, that portion will be construed in a manner to reflect, as
        nearly as possible, the original intention of the parties, and the
        remaining portions will remain in full force and effect. ATeams may
        transfer or assign this Agreement to any third party at its discretion
        and without notice. The Agreement is the final, complete and exclusive
        agreement of the parties with respect to the subject matter hereof and
        supersedes and merges all prior discussions between the parties with
        respect to such subject matter.
      </div>
      <SectionHeading>Updates: Terms of Service</SectionHeading>
      <div>
        In this archive you can see versions of our Terms of Service from 2020
        to the present.
        <ul>
          {TOS_HISTORY.map((tos) => (
            <li key={tos.label}>
              <a href={tos.url} target="_blank" rel="noopener noreferrer">
                {tos.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
